import { type ColorCodeConfig } from '@bizzdesign/modeler/models';
import {
  type ColorCodeType,
  type ColorValuesMapItem,
  type HSB,
  type RgbCode,
} from '@bizzdesign/shared/models';
import { limitTo } from './types.utils';
import {
  BDcolorGray10,
  BDcolorGray5,
  BDcolorInfographicsKey1,
  BDcolorInfographicsKey10,
  BDcolorInfographicsKey11,
  BDcolorInfographicsKey12,
  BDcolorInfographicsKey13,
  BDcolorInfographicsKey14,
  BDcolorInfographicsKey15,
  BDcolorInfographicsKey16,
  BDcolorInfographicsKey17,
  BDcolorInfographicsKey18,
  BDcolorInfographicsKey19,
  BDcolorInfographicsKey2,
  BDcolorInfographicsKey20,
  BDcolorInfographicsKey21,
  BDcolorInfographicsKey22,
  BDcolorInfographicsKey23,
  BDcolorInfographicsKey24,
  BDcolorInfographicsKey3,
  BDcolorInfographicsKey4,
  BDcolorInfographicsKey5,
  BDcolorInfographicsKey6,
  BDcolorInfographicsKey7,
  BDcolorInfographicsKey8,
  BDcolorInfographicsKey9,
} from '@bizzdesign/styles';

// color conversion functions taken from Unify
// with minimal improvements and added documentation

/**
 * Converts an individual RBG number into its hex value
 * @param c number
 * @returns hex string of passed number
 */
export const colorComponentToHex = (c: number): string => {
  const hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
};

/**
 * Converts RGB color structure into its hex code equivalent
 * @param param0 RGB code object
 * @param useHash flag whether to include the hashtag in front of the hex value
 * @returns hex string of the color
 */
export const colorRgbToHex = (
  { r, g, b }: RgbCode,
  useHash: boolean,
): string => {
  const hex = `${useHash ? '#' : ''}${colorComponentToHex(r)}${colorComponentToHex(g)}${colorComponentToHex(b)}`;
  return hex.toUpperCase();
};

/**
 * Converts a hex color code into its RGB equivalent
 * @param col hex string code of color
 * @returns RGBCodes object
 */
export const colorHexToRgb = (col: string): RgbCode => {
  if (col.startsWith('#')) {
    col = col.slice(1);
  }
  const num = Number.parseInt(col, 16);

  let r = num >> 16;
  if (r > 255) {
    r = 255;
  } else if (r < 0) {
    r = 0;
  }

  let g = (num >> 8) & 0x00ff;
  if (g > 255) {
    g = 255;
  } else if (g < 0) {
    g = 0;
  }

  let b = num & 0x0000ff;
  if (b > 255) {
    b = 255;
  } else if (b < 0) {
    b = 0;
  }

  return { r, g, b };
};

/**
 * Converts RGB numbers to HSB (hue, saturation, brightness) type
 * @param red numeric value of red
 * @param green numeric value of green
 * @param blue numeric value of blue
 * @returns a HSB object calculated based on RGB values
 */
export const rgbToHsb = (red: number, green: number, blue: number): HSB => {
  const cmax = Math.max(red, Math.max(green, blue));
  const cmin = Math.min(red, Math.min(green, blue));

  let s: number;
  let h: number;
  let b = cmax / 255;
  if (cmax === 0) {
    s = 0;
  } else {
    s = (cmax - cmin) / cmax;
  }
  if (s === 0) {
    h = 0;
  } else {
    const redc = (cmax - red) / (cmax - cmin);
    const greenc = (cmax - green) / (cmax - cmin);
    const bluec = (cmax - blue) / (cmax - cmin);
    if (red === cmax) {
      h = bluec - greenc;
    } else if (green === cmax) {
      h = 2 + redc - bluec;
    } else {
      h = 4 + greenc - redc;
    }
    h /= 6;
    if (h < 0) {
      h = h + 1;
    }
  }

  const offsetHue = h * 360;
  h = offsetHue < 0 ? (offsetHue % 360) + 360 : offsetHue % 360;
  s = limitTo(s * 100, 0, 100);
  b = limitTo(b * 100, 0, 100);
  return { h, s, b };
};

/**
 * Coverts a HSB object into an RGB object
 * @param param0 a HSB object
 * @returns RGB values based on the passed HSB
 */
export const hsbToRgb = ({
  h: hue,
  s: saturation,
  b: brightness,
}: HSB): RgbCode => {
  hue = (hue % 360) / 360;
  saturation = limitTo(saturation, 0, 100) / 100;
  brightness = limitTo(brightness, 0, 100) / 100;

  let r = 0;
  let g = 0;
  let b = 0;
  if (saturation === 0) {
    r = g = b = brightness * 255 + 0.5;
  } else {
    const h = (hue - Math.floor(hue)) * 6;
    const f = h - Math.floor(h);
    const p = brightness * (1 - saturation);
    const q = brightness * (1 - saturation * f);
    const t = brightness * (1 - saturation * (1 - f));
    switch (Math.floor(h)) {
      case 0:
        r = brightness * 255 + 0.5;
        g = t * 255 + 0.5;
        b = p * 255 + 0.5;
        break;
      case 1:
        r = q * 255 + 0.5;
        g = brightness * 255 + 0.5;
        b = p * 255 + 0.5;
        break;
      case 2:
        r = p * 255 + 0.5;
        g = brightness * 255 + 0.5;
        b = t * 255 + 0.5;
        break;
      case 3:
        r = p * 255 + 0.5;
        g = q * 255 + 0.5;
        b = brightness * 255 + 0.5;
        break;
      case 4:
        r = t * 255 + 0.5;
        g = p * 255 + 0.5;
        b = brightness * 255 + 0.5;
        break;
      case 5:
        r = brightness * 255 + 0.5;
        g = p * 255 + 0.5;
        b = q * 255 + 0.5;
        break;
      default:
        break;
    }
  }

  r = limitTo(Math.floor(r), 0, 255);
  g = limitTo(Math.floor(g), 0, 255);
  b = limitTo(Math.floor(b), 0, 255);
  return { r, g, b };
};

/**
 * Modifies the Hex/RGB codes based on the passed factor. This means it'll either brighten the color or darken it.
 * @param color an RGB object or Hex string
 * @param factor Factor for brightness. Lower the number, darker the color
 * @returns modified RBG object
 */
export const generateBorderColorViaHSB = (
  color: string | RgbCode,
  factor: number,
): RgbCode => {
  let rgb;
  if (typeof color === 'string') {
    rgb = colorHexToRgb(color);
  } else {
    rgb = color;
  }
  const r = Math.floor(rgb.r);
  const g = Math.floor(rgb.g);
  const b = Math.floor(rgb.b);
  // making darker, yet saturated color:
  const brightnessChange = -15;
  const saturationChange = +40;
  const hsb = rgbToHsb(r, g, b);
  // When performing a gradient on a gray/white color, avoid a color (saturation) change.
  // (This happens because of the HSB color space property where increasing the saturation
  //  of a white color moves it to red.)
  const saturationChange$ = r === g && r === b ? 0 : saturationChange;
  const newS = hsb.s + saturationChange$;
  const newb = (hsb.b + brightnessChange) * factor;
  const newRgb = hsbToRgb({
    h: hsb.h,
    s: newS,
    b: newb,
  });

  return newRgb;
};

export const isColorCodeNoValueCode = (code: string) =>
  code === 'white' || code === 'grey';

export const getDefaultColorCodeConfigs = (
  codes: readonly ColorCodeType[],
): ColorCodeConfig[] =>
  codes.map((code) => {
    if (isColorCodeNoValueCode(code)) {
      return {
        code,
        priority: 0,
      };
    } else {
      const priority = Number.parseInt(code.split('_')[1]);
      return {
        code,
        priority,
      };
    }
  });

export const getColorPriorityLevelForAttribute = (
  attrIndex: number,
  colorIndexRanges: number[],
): number => {
  if (colorIndexRanges.length === 1) {
    const benchmark = colorIndexRanges[0];
    return attrIndex < benchmark ? 1 : 2;
  }
  let priority = 1;
  for (const maxIndexForLevel of colorIndexRanges) {
    if (attrIndex < maxIndexForLevel) {
      break;
    } else {
      priority++;
    }
  }

  return priority;
};

// this exists because models cannot import variables from styles
export const DefaultColorValues: Record<ColorCodeType, ColorValuesMapItem> = {
  blue_1: { color: BDcolorInfographicsKey1 },
  blue_2: { color: BDcolorInfographicsKey9 },
  blue_3: { color: BDcolorInfographicsKey17 },
  green_1: { color: BDcolorInfographicsKey10 },
  green_2: { color: BDcolorInfographicsKey2 },
  green_3: { color: BDcolorInfographicsKey18 },
  yellow_1: { color: BDcolorInfographicsKey11 },
  yellow_2: { color: BDcolorInfographicsKey3 },
  yellow_3: { color: BDcolorInfographicsKey19 },
  orange_1: { color: BDcolorInfographicsKey12 },
  orange_2: { color: BDcolorInfographicsKey4 },
  orange_3: { color: BDcolorInfographicsKey20 },
  red_1: { color: BDcolorInfographicsKey13 },
  red_2: { color: BDcolorInfographicsKey5 },
  red_3: { color: BDcolorInfographicsKey21 },
  pink_1: { color: BDcolorInfographicsKey16 },
  pink_2: { color: BDcolorInfographicsKey8 },
  pink_3: { color: BDcolorInfographicsKey24 },
  purple_1: { color: BDcolorInfographicsKey14 },
  purple_2: { color: BDcolorInfographicsKey6 },
  purple_3: { color: BDcolorInfographicsKey22 },
  cyan_1: { color: BDcolorInfographicsKey15 },
  cyan_2: { color: BDcolorInfographicsKey7 },
  cyan_3: { color: BDcolorInfographicsKey23 },
  white: { color: BDcolorGray5 },
  grey: { color: BDcolorGray10 },
};
